import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQueries } from '@tanstack/react-query'
import { ArrowLeft, CheckCircle, Signature } from '@phosphor-icons/react'
import { Input, Select, TextArea } from '@/ui'
import { errorMessage, getOne } from '../../api/core'
import { useExceptions, useResolveException } from '../../api/exceptions'
import { useLot } from '../../api/gate'
import { ck } from '../../api/keys'
import { arr, kgToGrams, str } from '../../api/mappers'
import { mapPackLotDetail } from '../../api/pack'
import { useDisposeAll, useHolds, useLabReports } from '../../api/quality'
import { ActionButton } from '../../components/Can'
import {
  BoardColumns,
  BoardPage,
  FactRow,
  Facts,
  LinkButton,
  Note,
  PanelActions,
  Pill,
  SidePanel,
  TwoLine,
} from '../../components/board'
import { useUrlState } from '../../lib/useUrlState'
import { boardDate } from '../../model/payoutBoard'
import {
  dispositionBody,
  disposeBlocked,
  disposeTotals,
  rowProblem,
  WAIVER_NOTE_MIN,
  type DisposeBasis,
  type DisposeChoice,
  type DisposeRow,
} from '../../model/disposition'
import { kg2 } from '../../model/packing'
import { BoardCard } from '../packing/shared'
import { SignFields } from './shared'

interface Info {
  heldG: number | null
  pouchesAtHall: number | null
  dispatched: number
  done: boolean
}

/**
 * Dispose · <batch> family (alignment map V1-F7-B1c Dispose; F7-B1.c): the basis (a failing
 * confirming report, or a waiver in writing), one disposition per held item — sacks Red tie
 * (ordinary) or Destroy, a pack lot Destroy only with its pouch count — totals that must equal what
 * is held, then the signature (reason + password) and Close the case. The farmer's account is
 * never touched: his price was fixed at the gate.
 */
export function DisposePage() {
  const { unitId } = useParams()
  const [f] = useUrlState({ case: '' })
  const unit = useLot(unitId)
  const holds = useHolds(unitId)
  const family = useMemo(
    () => (holds.data?.held ?? []).filter((h) => h.scope !== 'SIBLING'),
    [holds.data],
  )
  const siblings = (holds.data?.held ?? []).filter((h) => h.scope === 'SIBLING')
  const reports = useLabReports({ unitId, limit: 20 }, Boolean(unitId))
  const failing = (reports.data?.rows ?? []).filter(
    (r) => r.overall === 'FAIL' && r.status !== 'SUPERSEDED' && r.status !== 'REJECTED',
  )
  const cases = useExceptions(
    { type: 'LAB_FAIL', status: 'OPEN', unitId, page: 1, limit: 5 },
    Boolean(unitId),
  )
  const caseId = f.case || cases.data?.rows[0]?.id || holds.data?.exceptionId || null

  const infos = useQueries({
    queries: family.map((h) => ({
      queryKey: [...ck.quality, 'dispose-info', h.unitId, h.packLotId],
      queryFn: (): Promise<Info> =>
        h.unitType === 'PACK' && h.packLotId ? packInfo(h.packLotId) : sackInfo(h.unitId),
    })),
  })
  const [edits, setEdits] = useState<
    Record<string, { choice: DisposeChoice; kg: string; pouches: string }>
  >({})
  const rows: DisposeRow[] = family.map((h, i) => {
    const info = infos[i]?.data
    const isPack = h.unitType === 'PACK'
    const e = edits[h.unitId]
    return {
      unitId: h.unitId,
      code: h.packLotCode ?? h.code,
      isPack,
      heldG: info?.heldG ?? null,
      pouchesAtHall: info?.pouchesAtHall ?? null,
      choice: e?.choice ?? (isPack ? 'DESTROY' : ''),
      kg: e?.kg ?? (info?.heldG ? (info.heldG / 1000).toFixed(3) : ''),
      pouches:
        e?.pouches ??
        (info?.pouchesAtHall !== null && info?.pouchesAtHall !== undefined
          ? String(info.pouchesAtHall)
          : ''),
      done: info?.done ?? false,
    }
  })
  const edit = (
    r: DisposeRow,
    patch: Partial<{ choice: DisposeChoice; kg: string; pouches: string }>,
  ) =>
    setEdits((x) => ({
      ...x,
      [r.unitId]: { choice: r.choice, kg: r.kg, pouches: r.pouches, ...x[r.unitId], ...patch },
    }))

  const [basis, setBasis] = useState<DisposeBasis>({
    basis: '',
    confirmingReportId: '',
    waiverNote: '',
  })
  const confirmingId =
    basis.confirmingReportId || (failing.length === 1 ? (failing[0]?.id ?? '') : '')
  const b: DisposeBasis = { ...basis, confirmingReportId: confirmingId }
  const [reason, setReason] = useState('')
  const [password, setPassword] = useState('')
  const dispose = useDisposeAll()
  const resolve = useResolveException()
  const totals = disposeTotals(rows)
  const blocked = infos.some((q) => q.isLoading)
    ? 'Reading what is held…'
    : disposeBlocked(rows, b, reason, password)
  const allDone = rows.length > 0 && rows.every((r) => r.done)
  const destroyed = rows.some((r) => r.choice === 'DESTROY')
  const dispatched = infos.reduce((s, q) => s + (q.data?.dispatched ?? 0), 0)
  const packRow = family.find((h) => h.unitType === 'PACK')

  const sign = async () => {
    if (blocked) return
    try {
      await dispose.mutateAsync({
        rows: rows
          .filter((r) => !r.done)
          .map((r) => ({ unitId: r.unitId, body: dispositionBody(r, b, reason) })),
        password,
      })
      setPassword('')
      await Promise.all(infos.map((q) => q.refetch()))
    } catch {
      setPassword('')
    }
  }

  return (
    <BoardPage
      title={`Dispose · ${unit.data?.code ?? '…'} family`}
      actions={
        <LinkButton
          to={`/c/quality/holds/${unitId}/trace-back`}
          icon={<ArrowLeft aria-hidden="true" />}
        >
          Trace back
        </LinkButton>
      }
    >
      {failing[0] ? (
        <Pill tone="bad">Fail confirmed · {failing[0].reportNo}</Pill>
      ) : (
        <Pill tone="wait">Waiting for the confirming result</Pill>
      )}
      <BoardColumns
        main={
          <>
            <BoardCard title="Basis">
              <label className="flex items-start gap-2 text-[15px]">
                <input
                  type="radio"
                  name="basis"
                  className="mt-1 h-5 w-5 accent-ink"
                  checked={b.basis === 'RETEST'}
                  onChange={() => setBasis((x) => ({ ...x, basis: 'RETEST' }))}
                />
                <span className="flex flex-1 flex-col gap-1">
                  <b>Confirming result</b>
                  {failing.length > 1 ? (
                    <Select
                      aria-label="Confirming report"
                      value={confirmingId}
                      onChange={(v) =>
                        setBasis((x) => ({ ...x, basis: 'RETEST', confirmingReportId: v }))
                      }
                      placeholder="Pick the report"
                      options={failing.map((r) => ({
                        value: r.id,
                        label: `${r.reportNo} · ${boardDate(r.testedAt)}`,
                      }))}
                    />
                  ) : (
                    <span className="text-[14px] text-muted">
                      {failing[0]
                        ? `${failing[0].reportNo} · ${failing[0].rowsNotPass} rows not pass · fail confirmed`
                        : 'No failing report on this batch yet.'}
                    </span>
                  )}
                </span>
              </label>
              <label className="flex items-start gap-2 text-[15px]">
                <input
                  type="radio"
                  name="basis"
                  className="mt-1 h-5 w-5 accent-ink"
                  checked={b.basis === 'WAIVED'}
                  onChange={() => setBasis((x) => ({ ...x, basis: 'WAIVED' }))}
                />
                <span className="flex flex-1 flex-col gap-1">
                  <b>Waiver in writing</b>
                  <span className="text-[14px] text-muted">
                    re-test waived by the quality person
                  </span>
                  {b.basis === 'WAIVED' && (
                    <TextArea
                      rows={2}
                      value={basis.waiverNote}
                      onChange={(e) => setBasis((x) => ({ ...x, waiverNote: e.target.value }))}
                      placeholder={`Why the re-test is waived (at least ${WAIVER_NOTE_MIN} letters)`}
                    />
                  )}
                </span>
              </label>
            </BoardCard>

            <div className="overflow-x-auto rounded-card border-[1.5px] border-rule bg-surface">
              <table className="w-full border-collapse text-[15px]">
                <caption className="sr-only">Dispositions</caption>
                <thead>
                  <tr className="text-left text-[13px] text-muted">
                    <th className="border-b-[1.5px] border-ink px-2.5 py-2">Held item</th>
                    <th className="border-b-[1.5px] border-ink px-2.5 py-2">Disposition</th>
                    <th className="border-b-[1.5px] border-ink px-2.5 py-2 text-right">Kilos</th>
                    <th className="border-b-[1.5px] border-ink px-2.5 py-2 text-right">Pouches</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.unitId} className="align-top">
                      <td className="border-b border-rule px-2.5 py-2">
                        <TwoLine
                          top={r.code}
                          sub={`${r.isPack ? 'pack lot' : 'brand batch'}${family[i]?.locationCode ? ` · at ${family[i].locationCode}` : ''}`}
                        />
                        {rowProblem(r) && !r.done && (
                          <span className="text-[13px] text-danger">{rowProblem(r)}</span>
                        )}
                      </td>
                      <td className="border-b border-rule px-2.5 py-2">
                        {r.done ? (
                          <Pill tone="ok">Done</Pill>
                        ) : r.isPack ? (
                          <span className="flex flex-col">
                            <b>Destroy</b>
                            <span className="text-[13px] text-muted">
                              the only choice for a pack lot
                            </span>
                          </span>
                        ) : (
                          <Select
                            aria-label={`Disposition for ${r.code}`}
                            value={r.choice}
                            placeholder="Choose"
                            onChange={(v) => edit(r, { choice: v as DisposeChoice })}
                            options={[
                              { value: 'DIVERT', label: 'Red tie (ordinary)' },
                              { value: 'DESTROY', label: 'Destroy' },
                            ]}
                          />
                        )}
                      </td>
                      <td className="border-b border-rule px-2.5 py-2 text-right">
                        {r.done ? (
                          '—'
                        ) : (
                          <Input
                            aria-label={`Kilos for ${r.code}`}
                            inputMode="decimal"
                            className="ml-auto h-9 w-28 text-right"
                            value={r.kg}
                            onChange={(e) => edit(r, { kg: e.target.value })}
                          />
                        )}
                      </td>
                      <td className="border-b border-rule px-2.5 py-2 text-right">
                        {r.isPack && !r.done ? (
                          <Input
                            aria-label={`Pouches for ${r.code}`}
                            inputMode="numeric"
                            className="ml-auto h-9 w-24 text-right"
                            value={r.pouches}
                            onChange={(e) =>
                              edit(r, { pouches: e.target.value.replace(/\D/g, '') })
                            }
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="font-bold">
                    <td className="px-2.5 py-2">
                      Total{' '}
                      <span className="block text-[13px] font-normal text-muted">
                        must equal what is held
                      </span>
                    </td>
                    <td />
                    <td className="px-2.5 py-2 text-right">
                      {kg2(totals.kgG)}
                      <span
                        className={`block text-[13px] font-normal ${totals.kgMatches ? 'text-ok' : 'text-danger'}`}
                      >
                        {totals.kgMatches ? '= held' : `held ${kg2(totals.heldG)}`}
                      </span>
                    </td>
                    <td className="px-2.5 py-2 text-right">
                      {totals.pouches || '—'}
                      {totals.pouchesHeld > 0 && (
                        <span
                          className={`block text-[13px] font-normal ${totals.pouchesMatch ? 'text-ok' : 'text-danger'}`}
                        >
                          {totals.pouchesMatch ? '= at hall' : `${totals.pouchesHeld} at hall`}
                        </span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
              {holds.isLoading && <p className="p-3 text-muted">Loading…</p>}
              {holds.error && <p className="p-3 text-danger">{errorMessage(holds.error)}</p>}
            </div>
            <Note>
              Sacks: Red tie (ordinary) or Destroy. A pack lot: Destroy only, with its pouch count;
              pouches are never emptied or relabelled as ours. Both moves are one-way.
            </Note>
            {siblings.length > 0 && (
              <Note className="text-ink">
                Her other lots ({siblings.map((s) => s.code).join(', ')} and what was made from
                them) stay held and follow only if her gate sample fails too.
              </Note>
            )}
            {packRow && (
              <Note className="text-ink">
                Already dispatched from {packRow.packLotCode}:{' '}
                {dispatched ? `${dispatched} pouches` : 'none'}. (Dispatched pouches of a disposed
                lot go to a recall, F9, and count as destroyed only when it accounts for them.)
              </Note>
            )}
          </>
        }
        side={
          <SidePanel label="Sign the disposition" title="Sign the disposition">
            <label className="flex flex-col gap-1 text-[15px]">
              <span className="font-bold">Reason</span>
              <TextArea
                rows={2}
                value={reason}
                placeholder="Why, in one or two lines"
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <SignFields password={password} onPassword={setPassword} />
            <PanelActions>
              <ActionButton
                perm="units:dispose"
                leadingIcon={<Signature />}
                blockedReason={allDone ? 'Everything held is disposed of.' : blocked}
                loading={dispose.isPending}
                onClick={() => void sign()}
              >
                Sign disposition
              </ActionButton>
              {!allDone && blocked && <Note>{blocked}</Note>}
            </PanelActions>
            <div className="mt-2 flex flex-col gap-1 border-t border-rule pt-3">
              <b className="text-[16px]">Close the case</b>
              <Facts>
                <FactRow label="Her account">unchanged</FactRow>
              </Facts>
              <Note>
                There is no way to deduct from the farmer: her price was fixed at the gate.
              </Note>
              <ActionButton
                perm="exceptions:resolve"
                variant="secondary"
                leadingIcon={<CheckCircle />}
                blockedReason={
                  !caseId
                    ? 'No open lab-fail case.'
                    : !allDone
                      ? 'After the disposition is signed.'
                      : null
                }
                loading={resolve.isPending}
                onClick={() =>
                  void resolve
                    .mutateAsync({
                      id: caseId!,
                      resolutionCode: destroyed ? 'DESTROYED' : 'DIVERTED',
                      note:
                        reason.trim().length >= 10
                          ? reason.trim()
                          : 'Held stock disposed of; disposition signed.',
                    })
                    .catch(() => undefined)
                }
              >
                Close case
              </ActionButton>
              <Note>After the disposition is signed.</Note>
            </div>
          </SidePanel>
        }
      />
    </BoardPage>
  )
}

async function packInfo(packLotId: string): Promise<Info> {
  const pl = await getOne(`/pack-lots/${packLotId}`, mapPackLotDetail)
  return {
    heldG: pl.pouchCount !== null ? pl.pouchCount * pl.netG : pl.packedG,
    pouchesAtHall: pl.pouchesLeft,
    dispatched: pl.pouchesDispatched,
    done: pl.status === 'DESTROYED' || pl.status === 'WITHDRAWN',
  }
}

async function sackInfo(unitId: string): Promise<Info> {
  return getOne(`/units/${unitId}`, (r) => {
    const bags = arr(r.bags).filter((b) => !['CONSUMED', 'VOID', 'MISSING'].includes(str(b.status)))
    const grams = bags.reduce(
      (s, b) => s + (kgToGrams((b.net_kg_current ?? b.net_kg_gate) as string) ?? 0),
      0,
    )
    return {
      heldG: grams,
      pouchesAtHall: null,
      dispatched: 0,
      done: str(r.status) === 'DESTROYED' || str(r.stream) === 'CONV' || bags.length === 0,
    }
  })
}
